<template>
  <div>
    <m-label>{{ label }}</m-label>
    <div class="custom-file">
      <m-input-group class="mt-0">
        <label
          ref="fileInputLabel"
          :data-button-text="buttonText"
          :class="getFileLabelClass"
          :for="id"
        >
          <span class="overflow-hidden">{{ placeholderModel }}</span>
        </label>
        <input
          ref="formFileInput"
          type="file"
          :class="getFileInputClass"
          :id="id"
          :aria-describedby="ariaDescribedby"
          :accept="accept"
          :capture="capture"
          :value="value"
          :title="placeholderModel"
          :multiple="multiple"
          @change="fileChanged"
        >
        <m-icon
          v-if="!multiple"
          ref="resetIcon"
          variant="reset"
          size="small"
          :class="fileCount ? null : 'opacity--transparent'"
          :is-suffix-icon="true"
          @click.native="resetFileInput"
        />
      </m-input-group>
      <small class="form-text">
        {{ helperText }}
      </small>
      <div class="flex-basis-100"></div>
      <small class="form-text text-danger">
        {{ errorText }}
      </small>
      <div
        v-for="(file, index) in selectedFiles"
        :key="`${file.name}-${index}`"
        ref="fileListWrapper"
        class="col-12 p-0 d-flex mb-3 align-items-end"
      >
        <m-icon variant="doc" />
        <p class="pl-2 custom-multi-file-name">{{ file.name }}</p>
        <m-icon
          class="custom-multi-file-reset-icon"
          variant="reset"
          @click.native="removeSelectedFiles(index)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MInputGroup from '../InputGroup';
import MIcon from '../Icon';
import MLabel from '../Label';

export default {
  name: 'MFormFile',
  components: {
    MInputGroup,
    MIcon,
    MLabel,
  },
  props: {
    /**
     * Used to set the `id` attribute on the rendered content, and used as the base to generate any additional element IDs as needed
    */
    id: {
      type: String,
      default: null,
    },
    /**
     * The ID of the element that provides a label for this component. Used as the value for the `aria-labelledby` attribute
     */
    ariaDescribedby: {
      type: String,
      default: null,
    },
    /**
     * Value to set on the file input's `accept` attribute
     */
    accept: {
      type: String,
      default: null,
    },
    /**
     * When set, will instruction the browser to use the devices camera (if supported)
     */
    capture: {
      type: Boolean,
      default: false,
    },
    /**
     * The current value of the file input. Will be a single `File` object or an array of `File` objects (if `multiple` or `directory` is set). Can be set to `null`, or an empty array to reset the file input
     */
    value: {
      type: [Array, File],
      default: null,
    },
    /**
     * Set the text of button to browse the file.
     */
    buttonText: {
      type: String,
      default: 'Choose File',
    },
    /**
     * Set the label of the form file. The label will be displayed above the form
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Set the placeholder of the form file if there is no file selected yet. This placeholder can just be set before mount. After mounting the component, the `placeholderModel` data will be used instead.
     */
    placeholder: {
      type: String,
      default: 'No file selected',
    },
    /**
     * Controls the validation state appearance of the component. `true` for valid, `false` for invalid, or `null` for no validation state
     */
    state: {
      type: Boolean,
      default: null,
    },
    /**
     * Set text helper below the form file
     */
    helperText: {
      type: String,
      default: '',
    },
    /**
     * Set the text that will be displayed if the state is `false` or error occured
     */
    errorText: {
      type: String,
      default: '',
    },
    /**
     * If set to `true`, when form file input is changed, it will emit File object as a payload. If set to `false`, it will emit the base64 of the file.
     */
    emitAsFile: {
      type: Boolean,
      default: false,
    },
    /**
     * If set to `true`, the form file will be able to select multiple file at once, and will always emit as `Array of File`.
     */
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    fileCount: 0,
    placeholderModel: '',
    selectedFiles: null,
  }),
  computed: {
    getFileInputClass() {
      return {
        'suffix-icon': true,
        'custom-file-input': true,
      };
    },
    getFileLabelClass() {
      return {
        'custom-file-label': true,
        'border-1-success': this.state,
        'border-1-danger': this.state === false,
        'text-dark': !this.multiple && this.fileCount,
      };
    },
    getMultiFilePlaceholder() {
      if (this.fileCount === 0) {
        return this.placeholder;
      }
      return this.fileCount === 1 ? `1 file selected` : `${this.fileCount} files selected`;
    },
  },
  mounted() {
    this.placeholderModel = this.placeholder;
  },
  methods: {
    removeSelectedFiles(index) {
      this.selectedFiles.splice(index, 1);
      this.fileCount = this.selectedFiles.length;
      this.placeholderModel = this.getMultiFilePlaceholder;
      this.$emit('change', this.selectedFiles);
      this.$emit('input', this.selectedFiles);
    },
    constructSelectedFile(fileList) {
      const output = [];
      fileList.forEach(file => output.push(file));
      return output;
    },
    fileChanged(e) {
      if (e.target.files.length > 0) {
        this.selectedFiles = this.multiple ? this.constructSelectedFile(e.target.files) : e.target.files[0];
        this.fileCount = e.target.files.length;
        this.placeholderModel = this.multiple ? this.getMultiFilePlaceholder : this.selectedFiles.name;
        let fileHandler = null;
        if (this.multiple) {
          fileHandler = this.selectedFiles;
          this.$emit('change', this.selectedFiles);
          this.$emit('input', this.selectedFiles);
        } else {
          fileHandler = new FileReader();
          fileHandler.readAsDataURL(this.selectedFiles);
          fileHandler.onload = event => {
            const fileEmitted = this.emitAsFile ? this.selectedFiles : event.target.result;
            this.$emit('change', fileEmitted);
            this.$emit('input', fileEmitted);
          };
        }
        return fileHandler;
      }
      this.placeholderModel = this.placeholder;
      this.fileCount = 0;
      this.$emit('change', null);
      this.$emit('input', null);
      return null;
    },
    resetFileInput() {
      this.$refs.formFileInput.value = null;
      this.fileCount = 0;
      this.placeholderModel = this.placeholder;
      this.$emit('change', null);
      this.$emit('input', null);
    },
  },
};
</script>
<style lang="scss" scoped>
.flex-basis-100 {
  flex-basis: 100%;
  height: 0;
}
.custom-file-label {
  white-space: nowrap;
  &::before {
    content: attr(data-button-text)
  }
}
.custom-multi-file {
  &-name {
    flex-grow: 1;
  }
  &-reset-icon {
    &:hover {
      cursor: pointer;
    }
  }
}
</style>
